/**
 * לקוח ה-API של הקטלוג הציבורי — TASKS 4.2.
 *
 * ⛔ ⛔ אף טיפוס כאן לא מכיל שדה מחיר — התגובות מהשרת עצמן לא מחזירות אותו
 *    (ראו `server/src/routes/public/catalog.ts`).
 */
import { parseErrorBody } from './apiError.js';

export { isPublicApiError } from './apiError.js';

export const PRICING_TYPES = ['unit', 'per_meter'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

export interface PublicCategory {
  id: number;
  name: string;
  slug: string;
  imageUrl: string | null;
}

export interface PublicProductImage {
  url: string;
  thumbUrl: string;
  cardUrl: string;
  alt: string;
}

export interface PublicProductListItem {
  id: number;
  sku: string;
  name: string;
  categoryName: string;
  categorySlug: string;
  image: PublicProductImage | null;
}

/**
 * ⭐ Feedback1.md סעיף 8 (20.08.2026) — כרטיס בקטלוג השטוח. מוצר בלי
 * ואריאנטים = כרטיס אחד (`variantId: null`). מוצר עם ואריאנטים = כרטיס
 * נפרד לכל ואריאנט, עם `name` שכבר כולל את תווית הוואריאנט (למשל
 * "שטוצר לתעלה — 8 צול").
 */
export interface CatalogCard {
  cardKey: string;
  sku: string;
  variantId: number | null;
  variantSku: string | null;
  variantLabel: string | null;
  name: string;
  description: string | null;
  categoryName: string;
  categorySlug: string;
  image: PublicProductImage | null;
}

export interface CatalogCardsPage {
  cards: CatalogCard[];
  total: number;
  hasMore: boolean;
}

export interface PublicMediaImage {
  id: number;
  variantId: number | null;
  type: 'image';
  url: string;
  thumbUrl: string;
  cardUrl: string;
  alt: string;
  isPrimary: boolean;
  width: number | null;
  height: number | null;
}

export interface PublicMediaVideo {
  id: number;
  variantId: number | null;
  type: 'video';
  url: string;
  posterUrl: string | null;
  alt: string;
  isPrimary: boolean;
  width: number | null;
  height: number | null;
}

export type PublicMedia = PublicMediaImage | PublicMediaVideo;

export interface PublicVariant {
  id: number;
  sku: string;
  label: string;
  attrs: Record<string, unknown> | null;
}

export interface PublicProductDetail {
  id: number;
  sku: string;
  name: string;
  description: string | null;
  specHtml: string | null;
  pricingType: PricingType;
  unitLabel: string | null;
  minOrderQty: string | null;
  qtyStep: string | null;
  priceIsEstimate: boolean;
  /** ⭐ מקביל אורתוגונלי ל-`priceIsEstimate` — מוצר בהזמנה אישית, ללא מחיר קבוע. */
  priceOnRequest: boolean;
  category: { id: number; name: string; slug: string };
}

export async function listCategories(): Promise<PublicCategory[]> {
  const res = await fetch('/api/categories');
  if (!res.ok) throw await parseErrorBody(res);
  const body = (await res.json()) as { categories: PublicCategory[] };
  return body.categories;
}

export async function getCategory(slug: string): Promise<PublicCategory> {
  const res = await fetch(`/api/categories/${encodeURIComponent(slug)}`);
  if (!res.ok) throw await parseErrorBody(res);
  const body = (await res.json()) as { category: PublicCategory };
  return body.category;
}

export async function listProducts(params: {
  category?: string;
  q?: string;
}): Promise<PublicProductListItem[]> {
  const search = new URLSearchParams();
  if (params.category) search.set('category', params.category);
  if (params.q) search.set('q', params.q);

  const res = await fetch(`/api/products?${search.toString()}`);
  if (!res.ok) throw await parseErrorBody(res);
  const body = (await res.json()) as { products: PublicProductListItem[] };
  return body.products;
}

export async function listCatalogCards(params: {
  limit: number;
  offset: number;
  q?: string;
}): Promise<CatalogCardsPage> {
  const search = new URLSearchParams();
  search.set('limit', String(params.limit));
  search.set('offset', String(params.offset));
  if (params.q) search.set('q', params.q);

  const res = await fetch(`/api/catalog/cards?${search.toString()}`);
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as CatalogCardsPage;
}

export async function getProduct(
  sku: string,
): Promise<{ product: PublicProductDetail; media: PublicMedia[]; variants: PublicVariant[] }> {
  const res = await fetch(`/api/products/${encodeURIComponent(sku)}`);
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    product: PublicProductDetail;
    media: PublicMedia[];
    variants: PublicVariant[];
  };
}
